// @ts-nocheck
/**
 * Agent My Leads — redesigned to obd3-gui pattern (ScrollPage + PageHeader +
 * per-assignment Card with its own server-paged table + shadcn Dialog for Complete).
 * Preserved: disposition-form dynamic fields, effective_wrap_up_seconds
 * countdown that auto-closes the modal on 0 (lead stays pending), agent
 * masking via cliDisplay.
 * Leads are paged from the server per assignment (an assignment can hold 50K);
 * the header only carries counts. Export pulls the full list once on click.
 */
import { useEffect, useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Check,
  Clock,
  Download,
  Loader2,
  Phone,
  RotateCcw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { cn } from '@/lib/utils/cn';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_AGENT_LEADS_MY,
  API_AGENT_LEADS_COMPLETE,
  API_AGENT_LEADS_MY_ASSIGNMENTS,
  API_AGENT_LEADS_MY_ITEMS,
} from '@/service/url';
import { LeadsPager, usePagedLeads } from './usePagedLeads';
import { getUser } from '@/utilities/setting';
import { getAgentMasking, maskNumber } from '@/utilities/cliDisplay';
import { MODULE_ACL } from '@/utilities/constant';
import { hasPermission, exportToCSV } from '@/utilities/helpers';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const FieldInput = ({ field, value, onChange }: any) => {
  const commonProps: any = {
    value: value ?? '',
    onChange: (e: any) => onChange(e.target.value),
  };
  switch (field.field_type) {
    case 'Integer':
    case 'Float':
      return (
        <Input
          type="number"
          step={field.field_type === 'Float' ? 'any' : '1'}
          min={field.field_min ?? undefined}
          max={field.field_max ?? undefined}
          {...commonProps}
        />
      );
    case 'Date':
      return <CalendarDateInput value={value ?? ''} onChange={onChange} />;
    case 'Boolean':
      return (
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox
            checked={!!value}
            onCheckedChange={(v: any) => onChange(!!v)}
          />
          Yes
        </label>
      );
    case 'Multi Select':
      return (
        <Select value={value ?? ''} onValueChange={(v) => onChange(v)}>
          <SelectTrigger>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt: any, i: number) => (
              <SelectItem key={i} value={String(opt)}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    case 'Long Text':
    case 'Textarea':
      return <Textarea {...commonProps} />;
    default:
      return <Input type="text" {...commonProps} />;
  }
};

const PAGE_SIZE = 25;

const GroupLeadsTable = ({ group, columns, reloadKey }: any) => {
  const [search, setSearch] = useState('');
  const paged = usePagedLeads(
    API_AGENT_LEADS_MY_ITEMS.replace(':assignmentId', group.assignment_id),
    { search },
    PAGE_SIZE,
  );
  const { reload } = paged;
  useEffect(() => {
    if (reloadKey) reload();
  }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const cols: any[] = columns(group, (paged.page - 1) * PAGE_SIZE);
  return (
    <div className="space-y-2">
      <Input
        className="max-w-xs"
        placeholder="Search name or number"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      <div className="overflow-hidden rounded-md border border-border">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr>
                {cols.map((c) => (
                  <th key={c.id} className="px-3 py-2 text-left font-medium" style={c.size ? { width: c.size } : undefined}>
                    {c.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.rows.length === 0 ? (
                <tr>
                  <td colSpan={cols.length} className="px-3 py-6 text-center text-muted-foreground">
                    {paged.loading ? 'Loading…' : search ? 'No leads match your search.' : 'No leads'}
                  </td>
                </tr>
              ) : (
                paged.rows.map((lead: any, index: number) => (
                  <tr key={lead.id} className="border-t border-border">
                    {cols.map((c) => (
                      <td key={c.id} className="px-3 py-2">
                        {c.cell({ row: { original: lead, index } })}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <LeadsPager page={paged.page} total={paged.total} limit={paged.limit} onPage={paged.setPage} />
      </div>
    </div>
  );
};

const AgentMyLeads = () => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);

  const [activeLead, setActiveLead] = useState<any>(null);
  const [formValues, setFormValues] = useState<any>({});
  const [submitting, setSubmitting] = useState(false);
  const [leadDeadline, setLeadDeadline] = useState<any>(null);
  const [leadSecondsLeft, setLeadSecondsLeft] = useState<any>(null);

  const [moduleOps, setModuleOps] = useState<any>({});
  const [reloadKey, setReloadKey] = useState(0);
  const [exporting, setExporting] = useState(false);

  const maskNumbers = getAgentMasking(getUser());
  const displayNumber = (num: any) => (maskNumbers ? maskNumber(num) : num);

  const applyAcl = (user: any) => {
    if (user?.acl_settings?.module_operations) {
      try {
        const parsed =
          typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
        setModuleOps(parsed);
      } catch (e) {
        console.log('Failed to parse module_operations', e);
      }
    }
  };

  useEffect(() => {
    applyAcl(getUser());
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => applyAcl(getUser());
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const aclLoaded = Object.keys(moduleOps).length > 0;
  const canRead = hasPermission(moduleOps, MODULE_ACL.AGENT_LEADS, 'read');
  const canEdit = !aclLoaded || hasPermission(moduleOps, MODULE_ACL.AGENT_LEADS, 'edit');

  const fetchLeads = (silent = false) => {
    if (!silent) setLoading(true);
    getApiCall(API_AGENT_LEADS_MY_ASSIGNMENTS)
      .then((resp: any) => setGroups(Array.isArray(resp) ? resp : []))
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to load leads'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchLeads();
  }, []);

  const closeLeadModal = () => {
    setActiveLead(null);
    setLeadDeadline(null);
    setLeadSecondsLeft(null);
  };

  const openComplete = (lead: any, group: any) => {
    const initial: any = {};
    (group.disposition_form?.fields || []).forEach((f: any) => {
      initial[f.field_name] =
        f.field_type === 'Boolean' ? false : f.field_default ?? '';
    });
    setFormValues(initial);
    setActiveLead({ lead, group });

    const eff = Number(group?.disposition_form?.effective_wrap_up_seconds);
    if (Number.isFinite(eff) && eff > 0) {
      setLeadDeadline(Date.now() + eff * 1000);
    } else {
      setLeadDeadline(null);
      setLeadSecondsLeft(null);
    }
  };

  useEffect(() => {
    if (!leadDeadline) {
      setLeadSecondsLeft(null);
      return undefined;
    }
    let firedOut = false;
    const tick = () => {
      const remainingMs = leadDeadline - Date.now();
      setLeadSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
      if (remainingMs <= 0 && !firedOut) {
        firedOut = true;
        setLeadDeadline(null);
        setLeadSecondsLeft(null);
        setActiveLead(null);
        toast.info('Time over — lead left pending. Open it again to complete.');
      }
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [leadDeadline]);

  const handleField = (name: string, val: any) =>
    setFormValues((prev: any) => ({ ...prev, [name]: val }));

  const handleComplete = () => {
    if (!canEdit) return toast.error("You don't have permission to complete leads");
    const fields = activeLead.group.disposition_form?.fields || [];
    for (const f of fields) {
      if (f.is_required) {
        const v = formValues[f.field_name];
        if (f.field_type === 'Boolean') continue;
        if (v === '' || v === null || v === undefined) {
          return toast.error(`${f.field_name} is required`);
        }
      }
    }

    setSubmitting(true);
    postApiCall(API_AGENT_LEADS_COMPLETE.replace(':itemId', activeLead.lead.id), {
      data: formValues,
    })
      .then(() => {
        toast.success('Lead completed');
        closeLeadModal();
        fetchLeads(true);
        setReloadKey((k) => k + 1);
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to complete lead'))
      .finally(() => setSubmitting(false));
  };

  const isCompleted = (lead: any) =>
    String(lead?.status || '').toLowerCase() === 'completed';

  const handleExport = () => {
    setExporting(true);
    getApiCall(API_AGENT_LEADS_MY)
      .then((full: any) => exportRows(Array.isArray(full) ? full : []))
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Unable to export leads'))
      .finally(() => setExporting(false));
  };

  const exportRows = (fullGroups: any[]) => {
    const rows = fullGroups.flatMap((g: any) =>
      (g.leads || []).map((l: any) => ({ group: g, lead: l })),
    );
    if (!rows.length) return toast.info('No leads to export');
    exportToCSV({
      fileName: 'my-leads',
      data: rows,
      columns: [
        {
          header: 'Assignment',
          accessor: (r: any) => r.group.title || r.group.contact_group || '-',
        },
        {
          header: 'Disposition Form',
          accessor: (r: any) => r.group.disposition_form?.name || '-',
        },
        { header: 'Assigned By', accessor: (r: any) => r.group.assigned_by || '-' },
        {
          header: 'Assigned At',
          accessor: (r: any) =>
            r.group.assigned_at ? new Date(r.group.assigned_at).toLocaleString() : '-',
        },
        { header: 'Contact Name', accessor: (r: any) => r.lead.contact_name || '-' },
        {
          header: 'Number',
          accessor: (r: any) => displayNumber(r.lead.contact_number) || '-',
        },
        {
          header: 'Status',
          accessor: (r: any) => (isCompleted(r.lead) ? 'Completed' : 'Pending'),
        },
      ],
    });
  };

  const totalPending = groups.reduce(
    (sum: number, g: any) => sum + (Number(g.pending_count) || 0),
    0,
  );

  if (aclLoaded && !canRead) {
    return (
      <ScrollPage className="max-w-none">
        <PageHeader title="My Leads" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </ScrollPage>
    );
  }

  const groupColumns = (group: any, offset = 0): ColumnDef<any>[] => [
    {
      id: 'num',
      header: '#',
      size: 50,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className="text-xs text-muted-foreground">{offset + row.index + 1}</span>
      ),
    },
    {
      id: 'contact_name',
      header: 'Contact Name',
      cell: ({ row }: any) => (
        <span className="font-medium">{row.original.contact_name || '—'}</span>
      ),
    },
    {
      id: 'contact_number',
      header: 'Number to Call',
      cell: ({ row }: any) => (
        <a
          href={`tel:${row.original.contact_number}`}
          className="inline-flex items-center gap-1.5 font-mono text-xs text-primary hover:underline"
        >
          <Phone className="h-3 w-3" />
          {displayNumber(row.original.contact_number)}
        </a>
      ),
    },
    {
      id: 'action',
      header: '',
      size: 140,
      enableSorting: false,
      cell: ({ row }: any) =>
        isCompleted(row.original) ? (
          <span className="inline-flex rounded-md bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
            Completed
          </span>
        ) : canEdit ? (
          <Button
            size="sm"
            variant="default"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            onClick={() => openComplete(row.original, group)}
          >
            <Check className="h-4 w-4" />
            Complete
          </Button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <ScrollPage className="max-w-none">
      <PageHeader
        title="My Leads"
        description={`${totalPending} pending lead(s) assigned to you`}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                fetchLeads();
                setReloadKey((k) => k + 1);
              }}
              disabled={loading}
            >
              <RotateCcw className={cn('h-4 w-4', loading && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={handleExport}
              disabled={loading || exporting || groups.length === 0}
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export
            </Button>
          </>
        }
      />

      {loading ? (
        <Loader />
      ) : groups.length === 0 ? (
        <div className="rounded-md border border-border bg-muted/30 p-10 text-center text-sm text-muted-foreground">
          No leads assigned to you right now.
        </div>
      ) : (
        <div className="space-y-4">
          {groups.map((group: any) => {
            const pending = Number(group.pending_count) || 0;
            return (
              <Card key={group.assignment_id}>
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <CardTitle className="text-base">
                        {group.title || group.contact_group || 'Leads'}
                      </CardTitle>
                      <div className="mt-1 text-xs text-muted-foreground">
                        Disposition Form:{' '}
                        <span className="font-medium text-foreground">
                          {group.disposition_form?.name || '—'}
                        </span>{' '}
                        · Assigned By:{' '}
                        <span className="font-medium text-foreground">
                          {group.assigned_by || '—'}
                        </span>{' '}
                        · Assigned At:{' '}
                        <span className="font-medium text-foreground">
                          {group.assigned_at
                            ? new Date(group.assigned_at).toLocaleString()
                            : '—'}
                        </span>
                      </div>
                      {group.transferred_at && (
                        <div className="mt-1 text-xs text-sky-700 dark:text-sky-400">
                          🔁 Transferred to you on{' '}
                          <span className="font-medium">
                            {new Date(group.transferred_at).toLocaleString()}
                          </span>
                          {group.transferred_by ? (
                            <>
                              {' '}
                              by <span className="font-medium">{group.transferred_by}</span>
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <span className="inline-flex rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400">
                      {pending} pending
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <GroupLeadsTable group={group} columns={groupColumns} reloadKey={reloadKey} />
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Complete lead modal */}
      <Dialog open={!!activeLead} onOpenChange={(o) => !o && closeLeadModal()}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              Complete Lead —{' '}
              {activeLead?.lead?.contact_name ||
                displayNumber(activeLead?.lead?.contact_number)}
            </DialogTitle>
          </DialogHeader>

          {typeof leadSecondsLeft === 'number' && (
            <div
              className={cn(
                'flex items-center justify-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold',
                leadSecondsLeft <= 10
                  ? 'border-destructive/30 bg-destructive/10 text-destructive'
                  : 'border-primary/30 bg-primary/10 text-primary',
              )}
              title="This form auto-closes when the timer runs out; the lead stays pending."
            >
              <Clock className="h-4 w-4" />
              {Math.floor(leadSecondsLeft / 60)}:
              {String(leadSecondsLeft % 60).padStart(2, '0')} left to submit
            </div>
          )}

          <div className="text-sm">
            <div>
              <span className="text-muted-foreground">Number:</span>{' '}
              <span className="font-mono">
                {displayNumber(activeLead?.lead?.contact_number)}
              </span>
            </div>
            <div>
              <span className="text-muted-foreground">Disposition Form:</span>{' '}
              <span className="font-medium">
                {activeLead?.group?.disposition_form?.name}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {(activeLead?.group?.disposition_form?.fields || []).map(
              (f: any, i: number) => (
                <div key={i} className="space-y-1.5">
                  <Label>
                    {f.field_name}
                    {f.is_required ? <span className="text-destructive"> *</span> : null}
                  </Label>
                  <FieldInput
                    field={f}
                    value={formValues[f.field_name]}
                    onChange={(val: any) => handleField(f.field_name, val)}
                  />
                </div>
              ),
            )}
            {(activeLead?.group?.disposition_form?.fields || []).length === 0 && (
              <div className="text-sm text-muted-foreground">
                This form has no fields. Submitting will mark the lead completed.
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeLeadModal} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleComplete}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              {submitting ? 'Submitting…' : 'Submit & Complete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ScrollPage>
  );
};

export default AgentMyLeads;
